import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert,
    Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
    InputLabel, Select, MenuItem, IconButton, Avatar, Fade, Chip, useTheme
} from '@mui/material';
import {
    ArrowBack, ClassOutlined, Add as AddIcon, Close as CloseIcon,
    Event as EventIcon, Description as DescriptionIcon
} from '@mui/icons-material';

const ModuloDetalhePagina = () => {
    const { id } = useParams();
    const [modulo, setModulo] = useState(null);
    const [turmasDoModulo, setTurmasDoModulo] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [modalAberto, setModalAberto] = useState(false);
    const [turmasSemModulo, setTurmasSemModulo] = useState([]);
    const [turmaSelecionadaId, setTurmaSelecionadaId] = useState('');
    const [sucesso, setSucesso] = useState('');
    const navegar = useNavigate();
    const theme = useTheme();

    const buscarDadosDoModulo = useCallback(async () => {
        try {
            setSucesso('');
            setErro('');
            const [respostaModulo, respostaTurmas] = await Promise.all([
                api.get(`/api/modulos/${id}`),
                api.get(`/api/modulos/${id}/turmas`)
            ]);
            setModulo(respostaModulo.data);
            setTurmasDoModulo(respostaTurmas.data);
        } catch (err) {
            setErro('Falha ao carregar os detalhes do módulo.');
        } finally {
            if (carregando) setCarregando(false);
        }
    }, [id, carregando]);

    useEffect(() => {
        buscarDadosDoModulo();
    }, [buscarDadosDoModulo]);

    const abrirModal = async () => {
        try {
            const resposta = await api.get('/api/turmas/sem-modulo');
            setTurmasSemModulo(resposta.data);
            setModalAberto(true);
        } catch (err) {
            setErro("Não foi possível carregar as turmas disponíveis.");
        }
    };

    const fecharModal = () => {
        setModalAberto(false);
        setTurmaSelecionadaId('');
    };

    const lidarComAssociacao = async () => {
        if (!turmaSelecionadaId) {
            alert("Por favor, selecione uma turma.");
            return;
        }
        try {
            await api.put(`/api/turmas/${turmaSelecionadaId}/associar-modulo`, {
                modulo_id: id
            });
            setSucesso('Turma associada com sucesso!');
            fecharModal();
            buscarDadosDoModulo();
        } catch (err) {
            setErro("Ocorreu um erro ao associar a turma.");
        }
    };

    const lidarComDesassociacao = async (turmaId, turmaNome) => {
        setModalConfirmacao({
            aberto: true,
            turmaId,
            turmaNome
        });
    };

    const [modalConfirmacao, setModalConfirmacao] = useState({
        aberto: false,
        turmaId: null,
        turmaNome: ''
    });

    const fecharModalConfirmacao = () => {
        setModalConfirmacao({
            aberto: false,
            turmaId: null,
            turmaNome: ''
        });
    };

    const confirmarDesassociacao = async () => {
        try {
            await api.put(`/api/turmas/${modalConfirmacao.turmaId}/desassociar-modulo`);
            setSucesso('Turma desassociada com sucesso!');
            setTurmasDoModulo(turmasAtuais =>
                turmasAtuais.filter(turma => turma.id !== modalConfirmacao.turmaId)
            );
            fecharModalConfirmacao();
        } catch (err) {
            setErro(err.response?.data?.erro || "Ocorreu um erro ao desassociar a turma.");
            fecharModalConfirmacao();
        }
    };

    const formatarData = (dataString) => {
        if (!dataString) return '';
        return new Date(dataString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (erro && !modulo) {
        return (
            <Container maxWidth="lg" sx={{ py: 5 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    {erro}
                </Alert>
            </Container>
        );
    }

    if (!modulo) {
        return (
            <Container maxWidth="lg" sx={{ py: 5 }}>
                <Typography variant="h5" sx={{ textAlign: 'center', color: cores.cinza.medio }}>
                    Módulo não encontrado.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 3,
                    boxShadow: cores.sombras.medio
                }}>
                    {/* Header azul */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar-modulos')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {modulo.nome}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    mt: 0.5
                                }}
                            >
                                <EventIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
                                {formatarData(modulo.data_inicio)} - {formatarData(modulo.data_fim)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Descrição do Módulo */}
                    {modulo.descricao && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                mb: 4,
                                background: '#f8f9fa',
                                borderRadius: 2,
                                border: `1px solid ${cores.cinza.claro}`,
                                boxShadow: cores.sombras.suave
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'transparent',
                                        color: cores.primaria.principal,
                                        mr: 2
                                    }}
                                >
                                    <DescriptionIcon />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            color: cores.primaria.principal,
                                            mb: 1
                                        }}
                                    >
                                        Sobre este Módulo
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                        {modulo.descricao}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}

                    {/* Turmas Associadas */}
                    <Box sx={{ mt: 4 }}>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 3,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Avatar sx={{ bgcolor: cores.primaria.principal, mr: 2 }}>
                                    <ClassOutlined />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="h2"
                                        sx={{
                                            fontWeight: 700,
                                            color: cores.primaria.principal,
                                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                        }}
                                    >
                                        Turmas Associadas
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {turmasDoModulo.length} {turmasDoModulo.length === 1 ? 'turma associada' : 'turmas associadas'}
                                    </Typography>
                                </Box>
                            </Box>

                            <Button
                                variant="contained"
                                onClick={abrirModal}
                                startIcon={<AddIcon />}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: cores.primaria.principal,
                                    fontWeight: 600,
                                    py: 1,
                                    px: 2,
                                    boxShadow: cores.sombras.suave,
                                    '&:hover': {
                                        bgcolor: cores.primaria.escuro,
                                    }
                                }}
                            >
                                Associar Turma
                            </Button>
                        </Box>

                        {sucesso && (
                            <Alert
                                severity="success"
                                sx={{
                                    mb: 3,
                                    borderRadius: 2,
                                    backgroundColor: cores.sucesso.claro,
                                    borderColor: cores.sucesso.medio,
                                    color: cores.sucesso.escuro,
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {sucesso}
                            </Alert>
                        )}

                        {erro && (
                            <Alert
                                severity="error"
                                sx={{
                                    mb: 3,
                                    borderRadius: 2,
                                    backgroundColor: cores.erro.claro,
                                    borderColor: cores.erro.medio,
                                    color: cores.erro.escuro,
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {erro}
                            </Alert>
                        )}

                        <Fade in={true} timeout={500}>
                            <TableContainer
                                component={Paper}
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: cores.sombras.suave,
                                    overflow: 'hidden',
                                    border: `1px solid ${cores.cinza.claro}`,
                                }}
                            >
                                <Table>
                                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Nome da Turma</TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Semestre/Ano</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>Ações</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {turmasDoModulo.length > 0 ? (
                                            turmasDoModulo.map((turma, index) => (
                                                <Fade in={true} timeout={300 + index * 100} key={turma.id}>
                                                    <TableRow
                                                        hover
                                                        sx={{
                                                            '&:hover': {
                                                                bgcolor: '#f8fafc',
                                                            }
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 36,
                                                                        height: 36,
                                                                        mr: 2,
                                                                        bgcolor: 'transparent',
                                                                        color: cores.primaria.principal,
                                                                        border: `2px solid ${cores.cinza.claro}`
                                                                    }}
                                                                >
                                                                    <ClassOutlined />
                                                                </Avatar>
                                                                <Typography sx={{ fontWeight: 600 }}>
                                                                    {turma.nome}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {turma.semestre ? (
                                                                <Chip
                                                                    label={turma.semestre}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: cores.cinza.claro,
                                                                        color: cores.cinza.escuro,
                                                                        fontWeight: 500
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: cores.cinza.medio, fontStyle: 'italic' }}>
                                                                    Não definido
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Button
                                                                size="small"
                                                                color="error"
                                                                variant="outlined"
                                                                onClick={() => lidarComDesassociacao(turma.id, turma.nome)}
                                                                startIcon={<CloseIcon />}
                                                                sx={{
                                                                    borderRadius: 2,
                                                                    textTransform: 'none',
                                                                    borderColor: cores.erro.principal,
                                                                    color: cores.erro.principal,
                                                                    '&:hover': {
                                                                        bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.08)`,
                                                                        borderColor: cores.erro.principal
                                                                    }
                                                                }}
                                                            >
                                                                Remover
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                </Fade>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                                    <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                        Nenhuma turma associada a este módulo ainda.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Fade>
                    </Box>
                </Paper>
            </Fade>

            {/* Modal de Associação de Turma */}
            <Dialog
                open={modalAberto}
                onClose={fecharModal}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: cores.sombras.forte
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.primaria.principal,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AddIcon sx={{ mr: 1 }} />
                        Associar Turma ao Módulo
                    </Box>
                    <IconButton
                        size="small"
                        onClick={fecharModal}
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.3)',
                            }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Box sx={{ pt: 1 }}>
                        <FormControl fullWidth margin="normal">
                            <InputLabel id="select-turma-label">Turmas Disponíveis</InputLabel>
                            <Select
                                labelId="select-turma-label"
                                value={turmaSelecionadaId}
                                label="Turmas Disponíveis"
                                onChange={(e) => setTurmaSelecionadaId(e.target.value)}
                                sx={{ borderRadius: 2 }}
                            >
                                {turmasSemModulo.length > 0 ? (
                                    turmasSemModulo.map((turma) => (
                                        <MenuItem key={turma.id} value={turma.id}>
                                            {turma.nome} {turma.semestre ? `(${turma.semestre})` : '(Sem semestre)'}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem disabled>Nenhuma turma livre encontrada.</MenuItem>
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={fecharModal}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            mr: 1,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComAssociacao}
                        variant="contained"
                        disabled={!turmaSelecionadaId}
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            boxShadow: cores.sombras.suave,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            },
                            '&.Mui-disabled': {
                                bgcolor: cores.cinza.medio,
                                color: 'white'
                            }
                        }}
                    >
                        Associar Turma
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal de Confirmação de Desassociação */}
            <Dialog
                open={modalConfirmacao.aberto}
                onClose={fecharModalConfirmacao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <CloseIcon fontSize="small" />
                    Confirmar Remoção
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Typography sx={{ mb: 2, color: cores.cinza.escuro }}>
                        Você está prestes a remover a turma <strong>{modalConfirmacao.turmaNome}</strong> deste módulo.
                    </Typography>
                    <Typography sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500
                    }}>
                        A turma não será excluída, apenas desassociada deste módulo.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharModalConfirmacao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmarDesassociacao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Remover Turma
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ModuloDetalhePagina;